import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink, Plus } from 'lucide-react';
import { Badge } from '@/components/ui/badge.jsx';
import TestBadge from '@/components/shared/TestBadge.jsx';
import NewIntakeSheet from '@/components/intake/NewIntakeSheet.jsx';
import { useShellStore } from '@/stores/shell-store.js';
import ClientRecord from './record/ClientRecord.jsx';
import QuotesPanel from './QuotesPanel.jsx';
import CommsTimelinePanel from './CommsTimelinePanel.jsx';
import DocumentsPanel from './DocumentsPanel.jsx';
import TrustLedgerPanel from './TrustLedgerPanel.jsx';
import CreditsPanel from './CreditsPanel.jsx';
import CertHoldersPanel from './CertHoldersPanel.jsx';
import StatsSummaryCard from './StatsSummaryCard.jsx';
import BlockedMarketsCard from './BlockedMarketsCard.jsx';
import AccountRail from './AccountRail.jsx';
import { toDate } from '@/lib/date-display.js';

// Account workspace (Task I.3) — the account-level overview re-homed into the
// TRACON shell (account-workspace.mockup.html): account header (breadcrumb ·
// title · badge · meta) → workbench of account-scoped panels → shell right
// rail (At a glance · Account · Carriers · Aircraft summary · Trust balance).
// Focused-deal views render DealWorkspace instead (account.jsx branches on
// dealId), so this component is account-scoped only — no deal action bar, no
// deal-scoped panels.
//
// The "Current term / Historical terms" sections (TermsPanel) are backed by
// GET /account/:id/terms (Task M) — the account's policies grouped current vs
// historical by Policy_End / stage.

export default function AccountWorkspace({ data, onChange }) {
  const setRail = useShellStore((s) => s.setRail);
  const clearRail = useShellStore((s) => s.clearRail);
  // MV-01 3.1 (Ship 1b): account-scoped "New intake" (deal-first broker intake).
  const [intakeOpen, setIntakeOpen] = useState(false);

  const resolve = data?.resolve || null;
  const fleetAndRoster = data?.fleetAndRoster || null;
  const stats = data?.stats || null;

  useEffect(() => {
    setRail(<AccountRail resolve={resolve} stats={stats} fleetAndRoster={fleetAndRoster} />);
    return () => clearRail();
  }, [resolve, stats, fleetAndRoster, setRail, clearRail]);

  if (!data) return null;

  const accountId = resolve?.account_id || resolve?.id || resolve?.account?.id || null;
  const title = resolve?.account_name || 'Account';
  const fleetCount = (fleetAndRoster?.fleet || []).filter((a) => a.status !== 'historical').length;
  const pilotCount = (fleetAndRoster?.roster || []).filter((p) => p.status !== 'historical').length;
  const sinceYear = stats?.earliest_bound_start
    ? toDate(stats.earliest_bound_start)?.getFullYear()
    : null;
  const currentCarrier = stats?.carriers_used?.find((c) => c.is_current)?.name || resolve?.current_carrier || null;

  const META = [
    sinceYear ? `Customer since ${sinceYear}${stats?.retention_years ? ` · ${stats.retention_years} yr retention` : ''}` : null,
    stats?.bound_term_count ? `${stats.bound_term_count} term${stats.bound_term_count === 1 ? '' : 's'}` : null,
    fleetCount || pilotCount ? `${fleetCount} aircraft · ${pilotCount} pilot${pilotCount === 1 ? '' : 's'}` : null,
    currentCarrier ? `Current carrier: ${currentCarrier}` : null,
  ].filter(Boolean);

  return (
    <div className="flex min-h-full flex-col">
      {/* ACCOUNT HEADER */}
      <header className="sticky top-0 z-10 border-b border-border bg-bg/95 px-4 pt-4 backdrop-blur md:px-6">
        <nav className="mb-2 flex items-center gap-1.5 text-[11px] text-text-dim">
          <Link to="/search" className="hover:text-text">Accounts</Link>
          <span className="opacity-50">/</span>
          <span className="text-text-muted">{title}</span>
        </nav>
        <div className="mb-2.5 flex items-center gap-3.5">
          <h1 className="truncate text-[19px] font-semibold tracking-tight text-cream">{title}</h1>
          <TestBadge isTest={resolve?.is_test} />
          {resolve?.account_type ? (
            <Badge variant="outline" className="uppercase tracking-wide">{resolve.account_type}</Badge>
          ) : null}
          <div className="ml-auto flex items-center gap-1">
            <button
              type="button"
              onClick={() => setIntakeOpen(true)}
              className="mr-1 flex h-7 items-center gap-1 rounded border border-border px-2 text-xs font-medium text-text-muted hover:bg-surface-strong hover:text-text"
            >
              <Plus size={13} strokeWidth={2} />
              New intake
            </button>
            <button
              type="button"
              title="Open in new tab"
              onClick={() => window.open(window.location.pathname, '_blank', 'noopener')}
              className="grid h-7 w-7 place-items-center rounded text-text-muted hover:bg-surface-strong hover:text-text"
            >
              <ExternalLink size={15} strokeWidth={1.5} />
            </button>
          </div>
          <NewIntakeSheet
            open={intakeOpen}
            onOpenChange={setIntakeOpen}
            accountId={accountId}
            accountName={title}
            onSuccess={() => onChange?.()}
          />
        </div>
        {META.length > 0 && (
          <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1 pb-3 text-xs text-text-muted">
            {META.map((m, i) => (
              <span key={i} className="flex items-center gap-2.5">
                {i > 0 && <span className="opacity-40">·</span>}
                {m}
              </span>
            ))}
          </div>
        )}
      </header>

      {/* WORKBENCH — the deal workspace's four-zone shape applied as an
          account rollup (MV-10 3.2): same headings, same order, account-
          scoped panels. WHAT NEEDS ME stays deal-scoped for now (an account
          rollup needs the account→deals map server-side; flagged for the
          Monday punch list rather than forced). */}
      <section className="mx-auto w-full max-w-[1000px] flex-1 space-y-3 px-4 py-4 md:px-6 md:py-5">
        {/* Mobile/tablet "at a glance" (rail only mounts at lg+). */}
        <div className="lg:hidden">
          <StatsSummaryCard stats={stats} />
        </div>

        {/* THE CLIENT RECORD (CLIENT-RECORD-SPEC §5): header facts, the
            terms chain, aircraft and people with provenance and fields in
            place. Replaces TermsPanel + FleetPanel + the account RosterPanel;
            the per-term roster stays on the deal page. */}
        <ClientRecord accountId={accountId} accountName={title} onChange={onChange} refreshKey={data} />
        {/* Blocked-market registry rollup (MV-02 item 4) — self-hides. */}
        <BlockedMarketsCard accountId={accountId} refreshKey={data} />

        <h2 className="pt-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-text-dim">Is the money right</h2>
        <TrustLedgerPanel ledger={data.trustLedger} />
        <CreditsPanel accountId={accountId} refreshKey={data} />

        <h2 className="pt-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-text-dim">Show me the paper</h2>
        <CertHoldersPanel accountId={accountId} />
        <DocumentsPanel documents={data.documents} accountId={accountId} />

        <h2 className="pt-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-text-dim">Machinery and history</h2>
        <QuotesPanel accountId={accountId} onChange={onChange} />
        <CommsTimelinePanel timeline={data.commsTimeline} accountId={accountId} onComposed={onChange} />
      </section>
    </div>
  );
}
